(() => {
  const CONTROLLER_KEY = '__evaFeatureBandStepController';
  const VERSION = 21;

  const DEFAULTS = {duration: 600};

  const HYSTERESIS = 0.06;
  const SHELL_TAKEOVER_HYSTERESIS = 8;
  const WHEEL_GESTURE_IDLE = 180;
  const WHEEL_DELTA_THRESHOLD = 12;
  /*
    Step chaining (opt-in via data-eva-step-chain). A new page requires the
    stream to settle (long quiet or a near-rest tick) and then a real attack.
    Decay, wobble, and sparse leftovers are Mac trackpad momentum — not a
    second swipe. Discrete mouse notches still have to be armed first.
  */
  const STEP_CHAIN_QUIET = 180;
  const STEP_CHAIN_REST = 8;
  const STEP_CHAIN_ATTACK = 16;
  const STEP_CHAIN_DISCRETE_GAP = 40;
  const STEP_CHAIN_DISCRETE_DELTA = 45;
  const STEP_CHAIN_GESTURE_IDLE = 360;

  const deckQuery = window.matchMedia('(min-width: 750px)');
  {

    const existing = window[CONTROLLER_KEY];
    if (existing?.version === VERSION) {
      existing.refresh?.();
      return;
    }
    existing?.destroy?.();
  }

  let bands = [];
  let activationFrame = 0;
  let resizeFrame = 0;
  let geometryObserver = null;
  const observedGeometry = new WeakSet();
  const shellOwners = new Set();
  const shellElementState = new Map();
  const mediaCompositingState = new Map();
  const wheelGesture = {
    active: false,
    accumulated: 0,
    bypass: false,
    direction: 0,
    endTimer: 0,
    handled: false,
    impulseArmed: false,
    impulseMagnitude: 0,
    impulseStamp: 0,
    lastEvent: 0,
    measuredEntries: new Set(),
  };

  function shellElements() {
    return [...new Set([
      ...document.querySelectorAll('header[data-mobile-sticky-menu-enabled]'),
      ...document.querySelectorAll('.centauri-eva-asuka .centauri-c01-product-bar-block--sticky'),
    ])];
  }

  function rescueShellFocus(entry) {
    const focused = document.activeElement;
    if (!focused || !shellElements().some((element) => element.contains(focused))) return;
    const stop = nearestStop(entry);
    const target = stop?.chapter ?? entry.band.querySelector('[data-eva-chapter]') ?? entry.band;
    target.setAttribute('tabindex', '-1');
    target.focus({preventScroll: true});
    if (document.activeElement === target) return;
    entry.band.setAttribute('tabindex', '-1');
    entry.band.focus({preventScroll: true});
  }

  function hideShellElements(entry) {
    rescueShellFocus(entry);
    shellElements().forEach((element) => {
      if (!shellElementState.has(element)) {
        shellElementState.set(element, {hadInert: element.hasAttribute('inert')});
      }
      element.setAttribute('inert', '');
    });
  }

  function restoreShellElements() {
    shellElementState.forEach((state, element) => {
      if (!state.hadInert) element.removeAttribute('inert');
    });
    shellElementState.clear();
  }

  function applyShellTakeover(entry) {
    if (entry.shellTakeoverActive) return;
    shellOwners.add(entry);
    entry.shellTakeoverActive = true;
    hideShellElements(entry);
    document.documentElement.dataset.evaShellTakeover = 'active';
  }

  function releaseShellTakeover(entry) {
    if (!entry.shellTakeoverActive) return;
    shellOwners.delete(entry);
    entry.shellTakeoverActive = false;
    if (shellOwners.size) {
      hideShellElements(shellOwners.values().next().value);
      return;
    }
    document.documentElement.removeAttribute('data-eva-shell-takeover');
    restoreShellElements();
  }

  function syncShellTakeover(entry, first, last) {
    if (!entry.shellTakeoverEnabled || entry.band.dataset.evaStep !== 'ready' || !first || !last) {
      releaseShellTakeover(entry);
      return;
    }
    const y = window.scrollY;
    const targetingBand = Number.isInteger(entry.stepTargetIndex)
      && y >= first.y - window.innerHeight
      && y <= last.y + window.innerHeight;
    const active = targetingBand
      || (y >= first.y - SHELL_TAKEOVER_HYSTERESIS
        && y <= last.y + SHELL_TAKEOVER_HYSTERESIS);
    if (active) applyShellTakeover(entry);
    else releaseShellTakeover(entry);
  }

  function syncShellReady() {
    const stepReady = bands.some((entry) => entry.band.dataset.evaStep === 'ready');
    document.documentElement.toggleAttribute('data-eva-step-scroll-ready', stepReady);
    document.documentElement.toggleAttribute(
      'data-eva-shell-takeover-ready',
      stepReady && bands.some((entry) => entry.shellTakeoverEnabled)
    );
  }

  function readMotion(band) {
    const value = Number.parseFloat(
      getComputedStyle(band).getPropertyValue('--eva-band-step-duration')
    );
    return {duration: Number.isFinite(value) && value > 0 ? value : DEFAULTS.duration};
  }

  function measure(entry) {
    if (!entry.geometryDirty && entry.measured) return entry.stops;
    entry.geometryDirty = false;
    entry.measured = true;

    const plan = [];
    entry.band.querySelectorAll('[data-eva-chapter]').forEach((chapter) => {
      const beats = Math.max(1, Number.parseInt(chapter.dataset.evaBeats ?? '1', 10) || 1);
      for (let beat = 0; beat < beats; beat += 1) plan.push({chapter, beat});
    });

    const bandTop = entry.band.getBoundingClientRect().top + window.scrollY;
    const stageHeight = entry.band.querySelector('[data-eva-band-deck]')?.offsetHeight ?? window.innerHeight;
    const step = plan.length
      ? (entry.band.dataset.evaStep === 'ready' && plan.length > 1
        ? (entry.band.offsetHeight - stageHeight) / (plan.length - 1)
        : entry.band.offsetHeight / plan.length)
      : 0;
    entry.bandTop = bandTop;
    entry.step = step;
    entry.stopCount = plan.length;
    entry.stops = plan.length >= 2 && step > 1
      ? plan.map((slot, index) => ({index, y: Math.round(bandTop + index * step), chapter: slot.chapter, beat: slot.beat}))
      : [];
    return entry.stops;
  }

  function nearestStop(entry, y = window.scrollY) {
    const measured = measure(entry);
    if (!measured.length) return null;
    const raw = Math.round((y - entry.bandTop) / entry.step);
    return measured[Math.min(measured.length - 1, Math.max(0, raw))];
  }

  function resetWheelGesture() {
    clearTimeout(wheelGesture.endTimer);
    wheelGesture.active = false;
    wheelGesture.accumulated = 0;
    wheelGesture.bypass = false;
    wheelGesture.direction = 0;
    wheelGesture.endTimer = 0;
    wheelGesture.handled = false;
    wheelGesture.impulseArmed = false;
    wheelGesture.impulseMagnitude = 0;
    wheelGesture.impulseStamp = 0;
    wheelGesture.lastEvent = 0;
    wheelGesture.measuredEntries.clear();
  }

  function scheduleWheelGestureEnd() {
    clearTimeout(wheelGesture.endTimer);
    const idle = chainCapable() ? STEP_CHAIN_GESTURE_IDLE : WHEEL_GESTURE_IDLE;
    wheelGesture.endTimer = window.setTimeout(resetWheelGesture, idle);
  }

  function normalizedWheelDelta(event) {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * window.innerHeight;
    return event.deltaY;
  }

  function freshStops(entry) {
    entry.geometryDirty = true;
    entry.measured = false;
    return measure(entry);
  }

  function gestureStops(entry, y, delta) {
    const cached = measure(entry);
    const first = cached[0];
    const last = cached.at(-1);
    if (!first || !last) return cached;
    const nextY = y + delta;
    const margin = window.innerHeight;
    if ((y < first.y - margin && nextY < first.y - margin)
      || (y > last.y + margin && nextY > last.y + margin)) return cached;
    if (wheelGesture.measuredEntries.has(entry)) return cached;
    wheelGesture.measuredEntries.add(entry);
    return freshStops(entry);
  }

  function lockedStepEntry() {
    const y = window.scrollY;
    return bands.find((entry) => {
      if (entry.band.dataset.evaStep !== 'ready' || !Number.isInteger(entry.stepTargetIndex)) return false;
      const measured = measure(entry);
      const first = measured[0];
      const last = measured.at(-1);
      if (!first || !last) return false;
      return y >= first.y - window.innerHeight && y <= last.y + window.innerHeight;
    }) ?? null;
  }

  function wheelIntent(delta) {
    const y = window.scrollY;
    const edge = 4;
    for (const entry of bands) {
      if (entry.band.dataset.evaStep !== 'ready') continue;
      const measured = gestureStops(entry, y, delta);
      const first = measured[0];
      const last = measured.at(-1);
      if (!first || !last) continue;
      if (y < first.y - edge) {
        if (delta > 0 && y + delta >= first.y - edge) return {entry, index: first.index};
        continue;
      }
      if (y > last.y + edge) {
        if (delta < 0 && y + delta <= last.y + edge) return {entry, index: last.index};
        continue;
      }
      const current = Number.isInteger(entry.stepTargetIndex)
        ? entry.stepTargetIndex
        : (nearestStop(entry, y)?.index ?? 0);
      if ((current === first.index && delta < 0 && y <= first.y + edge)
        || (current === last.index && delta > 0 && y >= last.y - edge)) {
        return {bypass: true, entry};
      }
      return {
        entry,
        index: Math.min(last.index, Math.max(first.index, current + Math.sign(delta))),
      };
    }
    return null;
  }

  function stepTo(entry, index) {
    const measured = freshStops(entry);
    const stop = measured[index];
    if (!stop) return;
    clearTimeout(entry.stepTargetTimer);
    cancelAnimationFrame(entry.stepCorrectionFrame);
    entry.stepTargetIndex = index;
    if (entry.shellTakeoverEnabled) applyShellTakeover(entry);
    window.scrollTo({
      top: stop.y,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    entry.stepCorrectionFrame = requestAnimationFrame(() => {
      entry.stepCorrectionFrame = requestAnimationFrame(() => {
        entry.stepCorrectionFrame = 0;
        if (entry.stepTargetIndex !== index) return;
        const corrected = freshStops(entry)[index];
        if (!corrected || Math.abs(corrected.y - stop.y) < 1) return;
        window.scrollTo({
          top: corrected.y,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    });
    entry.stepTargetTimer = window.setTimeout(() => {
      entry.stepTargetTimer = 0;
      entry.stepTargetIndex = null;
    }, (entry.motion?.duration ?? DEFAULTS.duration) + 250);
    if (entry.chainEnabled) wheelGesture.impulseArmed = false;
  }

  function chainCapable() {
    return bands.some((entry) => entry.chainEnabled && entry.band.dataset.evaStep === 'ready');
  }

  function trackChainImpulse(event, delta) {
    const magnitude = Math.abs(delta);
    const previousMagnitude = wheelGesture.impulseMagnitude;
    /*
      Gap uses event.timeStamp (hardware event time), not performance.now():
      main-thread jank batches queued momentum events, and a wall-clock gap
      would misread the flush as a fresh user impulse.
    */
    const gap = wheelGesture.impulseStamp ? event.timeStamp - wheelGesture.impulseStamp : Infinity;
    wheelGesture.impulseStamp = event.timeStamp;
    if (gap > STEP_CHAIN_QUIET || magnitude <= STEP_CHAIN_REST) {
      wheelGesture.impulseArmed = true;
    }
    const accelerated = wheelGesture.impulseArmed
      && magnitude >= STEP_CHAIN_ATTACK
      && magnitude >= Math.max(STEP_CHAIN_ATTACK, previousMagnitude * 1.8);
    const afterQuiet = wheelGesture.impulseArmed
      && magnitude >= STEP_CHAIN_ATTACK
      && gap > STEP_CHAIN_QUIET;
    const discrete = wheelGesture.impulseArmed
      && gap >= STEP_CHAIN_DISCRETE_GAP
      && magnitude >= STEP_CHAIN_DISCRETE_DELTA;
    const impulse = accelerated || afterQuiet || discrete;
    if (impulse) wheelGesture.impulseArmed = false;
    wheelGesture.impulseMagnitude = magnitude;
    return impulse;
  }

  function chainStep(entry, delta) {
    if (!Number.isInteger(entry.stepTargetIndex)) return;
    const measured = measure(entry);
    const first = measured[0];
    const last = measured.at(-1);
    if (!first || !last) return;
    const index = Math.min(last.index, Math.max(first.index, entry.stepTargetIndex + Math.sign(delta)));
    if (index === entry.stepTargetIndex) return;
    stepTo(entry, index);
  }

  function onWheel(event) {
    if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    const delta = normalizedWheelDelta(event);
    if (!delta) return;
    const now = performance.now();
    const chain = chainCapable();
    const stale = chain
      ? (wheelGesture.impulseStamp
        ? event.timeStamp - wheelGesture.impulseStamp > STEP_CHAIN_GESTURE_IDLE
        : !wheelGesture.active)
      : (!wheelGesture.active || now - wheelGesture.lastEvent > WHEEL_GESTURE_IDLE);
    if (stale) resetWheelGesture();
    if (!wheelGesture.active) wheelGesture.active = true;
    wheelGesture.lastEvent = now;
    scheduleWheelGestureEnd();
    const impulse = chainCapable() ? trackChainImpulse(event, delta) : false;
    const locked = lockedStepEntry();
    if (locked) {
      wheelGesture.handled = true;
      if (event.cancelable) event.preventDefault();
      if (impulse && locked.chainEnabled) chainStep(locked, delta);
      return;
    }
    if (wheelGesture.bypass) return;
    if (wheelGesture.handled) {
      if (!impulse) {
        if (event.cancelable) event.preventDefault();
        return;
      }
      wheelGesture.handled = false;
      wheelGesture.accumulated = 0;
      wheelGesture.direction = 0;
    }
    const intent = wheelIntent(delta);
    if (!intent) return;
    if (intent.bypass) {
      clearTimeout(intent.entry?.stepTargetTimer);
      if (intent.entry) {
        cancelAnimationFrame(intent.entry.stepCorrectionFrame);
        intent.entry.stepCorrectionFrame = 0;
        intent.entry.stepTargetTimer = 0;
        intent.entry.stepTargetIndex = null;
      }
      wheelGesture.bypass = true;
      return;
    }
    if (event.cancelable) event.preventDefault();
    const direction = Math.sign(delta);
    if (wheelGesture.direction && wheelGesture.direction !== direction) wheelGesture.accumulated = 0;
    wheelGesture.direction = direction;
    wheelGesture.accumulated += delta;
    if (Math.abs(wheelGesture.accumulated) < WHEEL_DELTA_THRESHOLD) return;
    wheelGesture.handled = true;
    stepTo(intent.entry, intent.index);
  }

  function beatElements(entry, stop) {
    if (!stop) return [];
    return [...stop.chapter.querySelectorAll(`[data-eva-beat="${stop.beat}"]`)];
  }

  function clearMediaCompositing(element) {
    const state = mediaCompositingState.get(element);
    if (state) {
      clearTimeout(state.timer);
      element.removeEventListener('transitionend', state.onEnd);
      mediaCompositingState.delete(element);
    }
    element.classList.remove('is-eva-media-compositing');
  }

  function armMediaCompositing(entry, element) {
    if (element.dataset.evaAnim !== 'media') return;
    const previous = mediaCompositingState.get(element);
    if (previous) {
      clearTimeout(previous.timer);
      element.removeEventListener('transitionend', previous.onEnd);
    }
    element.classList.add('is-eva-media-compositing');
    const onEnd = (event) => {
      if (event.target !== element || event.propertyName !== 'translate') return;
      clearMediaCompositing(element);
    };
    const timer = window.setTimeout(
      () => clearMediaCompositing(element),
      (entry.motion?.duration ?? DEFAULTS.duration) + 500
    );
    mediaCompositingState.set(element, {onEnd, timer});
    element.addEventListener('transitionend', onEnd);
  }

  function clearEntryMediaCompositing(entry) {
    entry.band.querySelectorAll('.is-eva-media-compositing').forEach(clearMediaCompositing);
  }

  function clearExit(entry) {
    clearTimeout(entry.exitTimer);
    entry.exitTimer = 0;
    (entry.exitElements ?? []).forEach((element) => {
      element.classList.remove('is-eva-leaving');
      clearMediaCompositing(element);
    });
    entry.exitElements = [];
    entry.exitChapter?.classList.remove('is-eva-outgoing');
    entry.exitChapter = null;
  }

  function beginExit(entry, previous, next) {
    clearExit(entry);
    entry.exitElements = beatElements(entry, previous);
    entry.exitElements.forEach((element) => {
      armMediaCompositing(entry, element);
      element.classList.add('is-eva-leaving');
    });
    if (previous.chapter !== next.chapter) {
      previous.chapter.classList.add('is-eva-outgoing');
      entry.exitChapter = previous.chapter;
    }
    entry.exitTimer = window.setTimeout(
      () => clearExit(entry),
      entry.motion?.duration ?? DEFAULTS.duration
    );
  }

  function rescueFocus(entry, stop) {
    const focused = document.activeElement;
    if (!focused || !entry.band.contains(focused)) return;
    const owner = focused.closest('[data-eva-chapter]');
    if (!owner || owner === stop.chapter) return;
    stop.chapter.setAttribute('tabindex', '-1');
    stop.chapter.focus({preventScroll: true});
  }

  function setCurrentChapter(entry, chapter) {
    entry.band.querySelectorAll('.is-eva-current').forEach((element) => {
      if (element !== chapter) element.classList.remove('is-eva-current');
    });
    chapter?.classList.add('is-eva-current');
  }

  function syncCharacter(entry) {
    if (entry.band.dataset.evaStep !== 'ready' || !entry.character) return;
    const state = window.scrollY >= entry.bandTop ? 'entered' : 'before';
    if (entry.characterState === state) return;
    const previous = entry.characterState;
    entry.characterState = state;
    entry.band.dataset.evaCharacterState = state;
    if (previous === null) {
      entry.characterSweepArmed = Boolean(entry.characterSweep && state === 'before');
      return;
    }
    clearTimeout(entry.characterMotionTimer);
    const playSweep = state === 'entered' && entry.characterSweepArmed;
    entry.band.dataset.evaCharacterMotion = state === 'entered'
      ? (playSweep ? 'enter' : 'enter-suppressed')
      : 'exit';
    if (state === 'entered') entry.characterSweepArmed = false;
    entry.characterMotionTimer = window.setTimeout(() => {
      entry.characterMotionTimer = 0;
      if (state === 'before' && entry.characterState === 'before') entry.characterSweepArmed = Boolean(entry.characterSweep);
      entry.band.removeAttribute('data-eva-character-motion');
    }, state === 'entered' ? 540 : 240);
  }

  function primeCharacter(entry) {
    if (!entry.character || entry.band.dataset.evaStep !== 'ready') return;
    cancelAnimationFrame(entry.characterPrimeFrame);
    entry.band.setAttribute('data-eva-character-initializing', '');
    syncCharacter(entry);
    void entry.character.offsetWidth;
    entry.characterPrimeFrame = requestAnimationFrame(() => {
      entry.characterPrimeFrame = 0;
      entry.band.removeAttribute('data-eva-character-initializing');
    });
  }

  function setActive(entry, index) {
    const measured = measure(entry);
    const stop = measured[index];
    if (!stop) return;
    if (index === entry.activeStop && beatElements(entry, stop).some((el) => el.classList.contains('is-eva-active'))) return;
    const previous = entry.activeStop !== null && entry.activeStop !== index
      ? measured[entry.activeStop]
      : null;
    if (previous) {

      const direction = index > entry.activeStop ? 'forward' : 'backward';
      if (entry.band.dataset.evaDirection !== direction) {
        entry.band.dataset.evaDirection = direction;
        void entry.band.offsetHeight;
      }
    }
    const nextElements = beatElements(entry, stop);
    entry.band.querySelectorAll('.is-eva-active').forEach((element) => element.classList.remove('is-eva-active'));
    if (previous) beginExit(entry, previous, stop);
    setCurrentChapter(entry, stop.chapter);
    nextElements.forEach((element) => armMediaCompositing(entry, element));
    nextElements.forEach((element) => element.classList.add('is-eva-active'));
    if (previous) rescueFocus(entry, stop);
    entry.activeStop = index;
    entry.band.dataset.evaActiveStop = String(index);
  }

  function syncActive(entry) {
    if (entry.measured) {
      const liveBandTop = entry.band.getBoundingClientRect().top + window.scrollY;
      if (Math.abs(liveBandTop - entry.bandTop) > 1) entry.geometryDirty = true;
    }
    const measured = measure(entry);
    if (!measured.length) {
      releaseShellTakeover(entry);
      return;
    }
    syncCharacter(entry);
    const first = measured[0];
    const last = measured.at(-1);
    syncShellTakeover(entry, first, last);
    if (window.scrollY < first.y - window.innerHeight || window.scrollY > last.y + window.innerHeight) {
      clearExit(entry);
      entry.band.removeAttribute('data-eva-active-stop');
      entry.band.removeAttribute('data-eva-direction');
      entry.band.querySelectorAll('.is-eva-active').forEach((element) => element.classList.remove('is-eva-active'));
      entry.band.querySelectorAll('.is-eva-current').forEach((element) => element.classList.remove('is-eva-current'));
      clearEntryMediaCompositing(entry);
      entry.activeStop = null;
      return;
    }
    let index = nearestStop(entry)?.index ?? 0;
    if (entry.activeStop !== null && index !== entry.activeStop) {
      const raw = (window.scrollY - entry.bandTop) / entry.step;
      if (Math.abs(raw - entry.activeStop) < 0.5 + HYSTERESIS) index = entry.activeStop;
    }
    setActive(entry, index);
  }

  function syncAll() {
    bands.forEach((entry) => syncActive(entry));
  }

  function onScroll() {
    if (activationFrame) return;
    activationFrame = requestAnimationFrame(() => {
      activationFrame = 0;
      syncAll();
    });
  }

  function invalidateGeometry() {
    bands.forEach((entry) => {
      entry.geometryDirty = true;
      entry.measured = false;
    });
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      refresh();
    });
  }

  function observeContentGeometry() {
    if (!('ResizeObserver' in window)) return;
    geometryObserver ??= new ResizeObserver(invalidateGeometry);
    bands.forEach((entry) => {
      entry.band.querySelectorAll('[data-eva-anim="copy"]').forEach((element) => {
        if (observedGeometry.has(element)) return;
        observedGeometry.add(element);
        geometryObserver.observe(element);
      });
    });
  }

  function disableEntry(entry) {
    releaseShellTakeover(entry);
    clearExit(entry);
    clearEntryMediaCompositing(entry);
    cancelAnimationFrame(entry.characterPrimeFrame);
    clearTimeout(entry.characterMotionTimer);
    clearTimeout(entry.stepTargetTimer);
    cancelAnimationFrame(entry.stepCorrectionFrame);
    entry.band.removeAttribute('data-eva-step');
    entry.band.removeAttribute('data-eva-active-stop');
    entry.band.removeAttribute('data-eva-direction');
    entry.band.removeAttribute('data-eva-character-state');
    entry.band.removeAttribute('data-eva-character-motion');
    entry.band.removeAttribute('data-eva-character-initializing');
    entry.band.querySelectorAll('.is-eva-active, .is-eva-current, .is-eva-outgoing').forEach((element) => {
      element.classList.remove('is-eva-active', 'is-eva-current', 'is-eva-outgoing');
    });
    entry.activeStop = null;
    entry.characterState = null;
    entry.characterSweepArmed = false;
    entry.characterPrimeFrame = 0;
    entry.characterMotionTimer = 0;
    entry.stepTargetIndex = null;
    entry.stepTargetTimer = 0;
    entry.stepCorrectionFrame = 0;
    syncTrack(entry);
  }

  function syncTrack(entry) {
    const track = entry.band.querySelector('[data-eva-band-track]');
    if (!track) return;
    const need = entry.band.dataset.evaStep === 'ready' ? Math.max(0, entry.stopCount - 1) : 0;
    while (track.childElementCount > need) track.lastElementChild.remove();
    while (track.childElementCount < need) {
      const spacer = document.createElement('div');
      spacer.className = 'eva-feature-band__stop';
      spacer.setAttribute('data-eva-band-stop', '');
      track.appendChild(spacer);
    }
  }

  function refresh() {
    resetWheelGesture();
    bands.forEach((entry) => disableEntry(entry));
    bands = [...document.querySelectorAll('[data-eva-band][data-eva-step-enabled]')].map((band) => ({
      band,
      stops: [],
      measured: false,
      geometryDirty: true,
      activeStop: null,
      motion: DEFAULTS,
      exitElements: [],
      exitChapter: null,
      exitTimer: 0,
      character: band.hasAttribute('data-eva-character-entrance')
        ? band.querySelector('[data-eva-band-character-shell]')
        : null,
      characterSweep: band.querySelector('[data-eva-band-character-sweep]'),
      characterState: null,
      characterSweepArmed: false,
      characterPrimeFrame: 0,
      characterMotionTimer: 0,
      shellTakeoverEnabled: band.hasAttribute('data-eva-shell-takeover-enabled'),
      shellTakeoverActive: false,
      chainEnabled: band.hasAttribute('data-eva-step-chain'),
      stepTargetIndex: null,
      stepTargetTimer: 0,
      stepCorrectionFrame: 0,
      bandTop: 0,
      step: 0,
      stopCount: 0,
    }));
    const deckCapable = deckQuery.matches && !window.Shopify?.designMode;

    const plans = bands.map((entry) => {
      entry.motion = readMotion(entry.band);
      const coherent = measure(entry).length >= 2;

      const deckSafe = coherent
        && entry.step >= window.innerHeight * 0.4
        && entry.step <= window.innerHeight + 8;
      return {entry, coherent, deckSafe};
    });
    plans.forEach(({entry, coherent, deckSafe}) => {
      if (deckCapable && coherent && deckSafe) {
        entry.band.dataset.evaStep = 'ready';
        primeCharacter(entry);
      }
      else disableEntry(entry);
      syncTrack(entry);
    });
    syncShellReady();
    observeContentGeometry();
    syncAll();
  }

  const readyHandler = () => refresh();
  document.addEventListener('DOMContentLoaded', readyHandler);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', refresh);
  document.addEventListener('shopify:section:select', refresh);
  window.addEventListener('resize', invalidateGeometry);
  window.addEventListener('scroll', onScroll, {passive: true});
  window.addEventListener('wheel', onWheel, {passive: false});
  deckQuery.addEventListener('change', refresh);
  window.visualViewport?.addEventListener('resize', invalidateGeometry);
  if (document.readyState !== 'loading') refresh();
  document.fonts?.ready.then(() => {
    if (window[CONTROLLER_KEY]?.version === VERSION) invalidateGeometry();
  });

  window[CONTROLLER_KEY] = {
    version: VERSION,
    refresh,
    destroy() {
      cancelAnimationFrame(activationFrame);
      cancelAnimationFrame(resizeFrame);
      geometryObserver?.disconnect();
      geometryObserver = null;
      bands.forEach((entry) => disableEntry(entry));
      bands = [];
      document.removeEventListener('DOMContentLoaded', readyHandler);
      document.removeEventListener('shopify:section:load', refresh);
      document.removeEventListener('shopify:section:unload', refresh);
      document.removeEventListener('shopify:section:select', refresh);
      window.removeEventListener('resize', invalidateGeometry);
      window.removeEventListener('scroll', onScroll, {passive: true});
      window.removeEventListener('wheel', onWheel, {passive: false});
      deckQuery.removeEventListener('change', refresh);
      window.visualViewport?.removeEventListener('resize', invalidateGeometry);
      document.documentElement.removeAttribute('data-eva-shell-takeover-ready');
      document.documentElement.removeAttribute('data-eva-shell-takeover');
      document.documentElement.removeAttribute('data-eva-step-scroll-ready');
      resetWheelGesture();
      restoreShellElements();
      delete window[CONTROLLER_KEY];
    },
    getState() {
      const enabled = bands.filter((entry) => entry.band.dataset.evaStep === 'ready');
      const first = enabled[0] ?? bands[0] ?? null;
      return {
        activeStop: first?.activeStop ?? null,
        characterState: first?.characterState ?? null,
        shellTakeoverActive: first?.shellTakeoverActive ?? false,
        shellTakeoverOwnerCount: shellOwners.size,
        wheelGestureActive: wheelGesture.active,
        wheelGestureHandled: wheelGesture.handled,
        stepChainEnabled: first?.chainEnabled ?? false,
        stepTargetIndex: first?.stepTargetIndex ?? null,
        enabled: enabled.length > 0,
        state: 'idle',
        stops: first ? measure(first).map(({index, y}) => ({index, y})) : [],
        bands: bands.map((entry) => ({
          enabled: entry.band.dataset.evaStep === 'ready',
          activeStop: entry.activeStop,
          characterState: entry.characterState,
          shellTakeoverActive: entry.shellTakeoverActive,
          stops: measure(entry).map(({index, y}) => ({index, y})),
        })),
      };
    },
  };
})();
